import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, CheckCircle2, XCircle, Inbox, Sparkles, WifiOff, Wifi, Clapperboard, Flame, Music, MessageSquare, Heart, Gift } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const POLL_MS = 20000;

function timeAgo(iso) {
  if (!iso) return "";
  const ms = Date.now() - new Date(iso).getTime();
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

export default function NotificationBell() {
  const [data, setData] = useState({ events: [], unread_count: 0, today_approved: 0, today_rejected: 0 });
  const [conn, setConn] = useState({ events: [], unread_count: 0 });
  const [pipe, setPipe] = useState({ events: [], unread_count: 0 });
  const [eng, setEng] = useState({ items: [], unread: 0 });
  const lastEngIso = useRef("");
  const [open, setOpen] = useState(false);
  const lastSeenIso = useRef("");
  const lastConnIso = useRef("");
  const lastPipeIso = useRef("");
  const initialised = useRef(false);
  const ref = useRef(null);
  const navigate = useNavigate();

  // Update #261 · One-click air straight from the alert
  const [airing, setAiring] = useState(null);
  const airHotTake = async (e) => {
    const id = e.meta?.hot_take_id;
    if (!id) { navigate(e.link || "/dashboard/news-ingestion"); return; }
    setAiring(id);
    const tid = toast.loading("Airing Hot Take — generating script + queueing socials…");
    try {
      const r = await api.post(`/news-ingestion/hot-takes/${id}/publish`);
      toast.success(r.data.already_published
        ? "Already aired — the script is ready"
        : "🔥 On-air script + socials queued", {
        id: tid,
        action: { label: "View", onClick: () => navigate("/dashboard/news-ingestion") },
      });
    } catch {
      toast.error("Couldn't air it — open News Ingestion to retry", { id: tid });
      navigate("/dashboard/news-ingestion");
    } finally { setAiring(null); }
  };

  const loadPipe = async ({ silent = false } = {}) => {
    try {
      const { data: c } = await api.get("/notifications/pipeline-alerts");
      if (initialised.current && !silent) {
        const fresh = (c.events || []).filter((e) => (e.created_at || "") > lastPipeIso.current);
        fresh.slice(0, 3).forEach((e) => {
          const isHot = e.kind === "hot_take";
          const msg = isHot
            ? `🔥 Hot Take: ${e.title}`
            : e.ok
            ? `Pipeline complete: ${e.title}`
            : `Pipeline finished with errors: ${e.title}`;
          const desc = e.detail || (e.steps_total ? `${e.steps_ok}/${e.steps_total} steps OK` : "");
          const dest = e.link || `/dashboard/vision-studio/project/${e.project_id}`;
          (e.ok ? toast.success : toast.warning)(msg, {
            description: desc,
            action: isHot && e.meta?.hot_take_id
              ? { label: "Air now", onClick: () => airHotTake(e) }
              : { label: "Open", onClick: () => navigate(dest) },
          });
          // Browser notification when the tab is in the background
          if (document.hidden && typeof Notification !== "undefined" && Notification.permission === "granted") {
            try { new Notification("BroadcastFlow AI", { body: `${msg}${desc ? ` — ${desc}` : ""}` }); } catch { /* */ }
          }
        });
      }
      const newest = (c.events || [])[0]?.created_at || lastPipeIso.current;
      if (newest) lastPipeIso.current = newest;
      setPipe(c);
    } catch { /* network blips are fine */ }
  };

  const loadConn = async ({ silent = false } = {}) => {
    try {
      const { data: c } = await api.get("/notifications/connection-alerts");
      if (initialised.current && !silent) {
        const fresh = (c.events || []).filter((e) => (e.created_at || "") > lastConnIso.current);
        fresh.slice(0, 3).forEach((e) => {
          if (e.kind === "down") {
            toast.error(`Connection lost: ${e.label}`, {
              description: e.detail || "The integration stopped responding.",
              action: { label: "Fix", onClick: () => navigate("/dashboard/integrations") },
            });
          } else {
            toast.success(`Connection restored: ${e.label}`);
          }
        });
      }
      const newest = (c.events || [])[0]?.created_at || lastConnIso.current;
      if (newest) lastConnIso.current = newest;
      setConn(c);
    } catch { /* network blips are fine */ }
  };

  const engDest = (t) => t === "new_request" ? "/dashboard/engagement/requests"
    : t === "new_dedication" ? "/dashboard/engagement/dedications"
    : t === "new_contest_entry" || t === "contest_winner" ? "/dashboard/engagement/contests"
    : t === "redemption_request" ? "/dashboard/engagement/rewards"
    : t === "voice_message" ? "/dashboard/engagement/voice"
    : "/dashboard/engagement/inbox";

  const loadEng = async ({ silent = false } = {}) => {
    try {
      const { data: c } = await api.get("/engagement/notifications");
      if (initialised.current && !silent) {
        const fresh = (c.items || []).filter((e) => !e.read && (e.created_at || "") > lastEngIso.current);
        fresh.slice(0, 3).forEach((e) => {
          toast.info(e.title, {
            action: { label: "Open", onClick: () => navigate(engDest(e.type)) },
          });
        });
      }
      const newest = (c.items || [])[0]?.created_at || lastEngIso.current;
      if (newest) lastEngIso.current = newest;
      setEng({ items: c.items || [], unread: c.unread || 0 });
    } catch { /* network blips are fine */ }
  };

  const load = async ({ silent = false } = {}) => {
    try {
      const { data: d } = await api.get("/notifications/sponsor-approvals");
      // Surface a toast when a NEW decision arrives after first load
      if (initialised.current && !silent) {
        const fresh = (d.events || []).filter((e) => (e.decided_at || "") > lastSeenIso.current);
        fresh.slice(0, 3).forEach((e) => {
          if (e.status === "approved") {
            toast.success(`Approved: ${e.advertiser} (${e.duration_sec}s)`, {
              description: e.feedback || "Sponsor cleared the spot for air.",
              action: {
                label: "View",
                onClick: () => navigate(`/dashboard/commercials?focus=${e.commercial_id}`),
              },
            });
          } else if (e.status === "rejected") {
            toast.error(`Changes requested: ${e.advertiser}`, {
              description: e.feedback || "Sponsor sent feedback.",
              action: {
                label: "View",
                onClick: () => navigate(`/dashboard/commercials?focus=${e.commercial_id}`),
              },
            });
          }
        });
      }
      // Update local "high-water mark" so a single decision doesn't toast twice
      const newest = (d.events || [])[0]?.decided_at || lastSeenIso.current;
      if (newest) lastSeenIso.current = newest;
      initialised.current = true;
      setData(d);
    } catch {
      /* network blips are fine */
    }
  };

  useEffect(() => {
    load({ silent: true });
    loadConn({ silent: true });
    loadPipe({ silent: true });
    loadEng({ silent: true });
    const id = setInterval(() => { load(); loadConn(); loadPipe(); loadEng(); }, POLL_MS);
    return () => clearInterval(id);
  }, []);

  // close popover on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const markSeen = async () => {
    try {
      await api.post("/notifications/sponsor-approvals/mark-seen");
      setData((d) => ({ ...d, unread_count: 0 }));
    } catch { /* */ }
    try {
      await api.post("/notifications/connection-alerts/mark-seen");
      setConn((c) => ({ ...c, unread_count: 0 }));
    } catch { /* */ }
    try {
      await api.post("/notifications/pipeline-alerts/mark-seen");
      setPipe((c) => ({ ...c, unread_count: 0 }));
    } catch { /* */ }
    try {
      await api.post("/engagement/notifications/read");
      setEng((c) => ({ ...c, unread: 0 }));
    } catch { /* */ }
  };

  const onOpen = () => {
    const next = !open;
    setOpen(next);
    if (next && (data.unread_count > 0 || conn.unread_count > 0 || pipe.unread_count > 0 || eng.unread > 0)) markSeen();
  };

  const unread = (data.unread_count || 0) + (conn.unread_count || 0) + (pipe.unread_count || 0) + (eng.unread || 0);

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={onOpen}
        className="relative inline-flex items-center justify-center h-9 w-9 rounded-md text-ink-secondary hover:text-cyan-300 hover:bg-bg-elevated transition"
        data-testid="notification-bell"
        aria-label="Notifications"
      >
        <Bell className="h-4 w-4" />
        {unread > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-brand-amber text-black text-[9px] font-bold font-mono flex items-center justify-center amber-glow"
            data-testid="notification-badge"
          >
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 top-11 z-30 w-[360px] rounded-xl border border-line bg-bg-surface shadow-2xl overflow-hidden"
          data-testid="notification-popover"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-line">
            <div className="flex items-center gap-2">
              <Inbox className="h-4 w-4 text-brand-amber" />
              <span className="font-display font-bold text-sm">Sponsor activity</span>
            </div>
            <div className="text-[10px] font-mono text-ink-muted">
              <span className="text-emerald-400">{data.today_approved}</span> approved ·{" "}
              <span className="text-rose-400">{data.today_rejected}</span> changes today
            </div>
          </div>

          <div className="max-h-[420px] overflow-y-auto">
            {(eng.items || []).length > 0 && (
              <div data-testid="notification-eng-section">
                <div className="px-4 pt-2.5 pb-1 font-mono text-[9px] uppercase tracking-[0.2em] text-ink-muted">Listener engagement</div>
                {eng.items.slice(0, 5).map((e) => (
                  <button
                    key={e.notification_id}
                    onClick={() => { setOpen(false); navigate(engDest(e.type)); }}
                    data-testid={`notification-eng-${e.notification_id}`}
                    className="w-full text-left px-4 py-3 border-b border-line/60 hover:bg-bg-elevated/60 transition flex items-start gap-3"
                  >
                    <div className={`shrink-0 h-7 w-7 rounded-md flex items-center justify-center ${
                      e.type === "new_request" ? "bg-cyan-500/15 text-cyan-400"
                      : e.type === "new_dedication" ? "bg-pink-500/15 text-pink-400"
                      : e.type === "redemption_request" ? "bg-emerald-500/15 text-emerald-400"
                      : "bg-amber-500/15 text-amber-400"}`}>
                      {e.type === "new_request" ? <Music className="h-3.5 w-3.5" />
                        : e.type === "new_dedication" ? <Heart className="h-3.5 w-3.5" />
                        : e.type === "redemption_request" ? <Gift className="h-3.5 w-3.5" />
                        : <MessageSquare className="h-3.5 w-3.5" />}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-semibold text-ink-primary truncate">{e.title}</div>
                      <div className="text-[10px] font-mono text-ink-muted mt-0.5">{timeAgo(e.created_at)}</div>
                    </div>
                  </button>
                ))}
              </div>
            )}
            {(pipe.events || []).length > 0 && (
              <div data-testid="notification-pipe-section">
                <div className="px-4 pt-2.5 pb-1 font-mono text-[9px] uppercase tracking-[0.2em] text-ink-muted">Pipeline runs</div>
                {pipe.events.slice(0, 4).map((e) => (
                  <div
                    key={e.alert_id}
                    data-testid={`notification-pipe-${e.alert_id}`}
                    className="w-full text-left px-4 py-3 border-b border-line/60 hover:bg-bg-elevated/60 transition flex items-start gap-3 cursor-pointer"
                    onClick={() => { setOpen(false); navigate(e.link || `/dashboard/vision-studio/project/${e.project_id}`); }}
                  >
                    <div className={`shrink-0 h-7 w-7 rounded-md flex items-center justify-center ${
                      e.kind === "hot_take" ? "bg-orange-500/15 text-orange-400"
                      : e.ok ? "bg-emerald-500/15 text-emerald-400" : "bg-amber-500/15 text-amber-400"
                    }`}>
                      {e.kind === "hot_take" ? <Flame className="h-3.5 w-3.5" /> : <Clapperboard className="h-3.5 w-3.5" />}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-semibold text-ink-primary truncate">
                        {e.kind === "hot_take" ? "Hot Take" : e.ok ? "Complete" : "Finished with errors"} · {e.title}
                      </div>
                      <div className="text-xs text-ink-secondary truncate">{e.detail || (e.steps_total ? `${e.steps_ok}/${e.steps_total} steps OK` : "")}</div>
                      <div className="text-[10px] font-mono text-ink-muted mt-0.5">{timeAgo(e.created_at)}</div>
                    </div>
                    {e.kind === "hot_take" && e.meta?.hot_take_id && (
                      <button
                        onClick={(ev) => { ev.stopPropagation(); airHotTake(e); }}
                        disabled={airing === e.meta.hot_take_id}
                        data-testid={`notification-air-${e.alert_id}`}
                        className="shrink-0 self-center rounded-full bg-brand-amber px-2.5 py-1 text-[10px] font-bold text-black hover:opacity-90 transition disabled:opacity-50"
                      >
                        {airing === e.meta.hot_take_id ? "Airing…" : "Air now"}
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
            {(conn.events || []).length > 0 && (
              <div data-testid="notification-conn-section">
                <div className="px-4 pt-2.5 pb-1 font-mono text-[9px] uppercase tracking-[0.2em] text-ink-muted">Connection health</div>
                {conn.events.slice(0, 5).map((e) => (
                  <button
                    key={e.alert_id}
                    onClick={() => { setOpen(false); navigate("/dashboard/integrations"); }}
                    data-testid={`notification-conn-${e.alert_id}`}
                    className="w-full text-left px-4 py-3 border-b border-line/60 hover:bg-bg-elevated/60 transition flex items-start gap-3"
                  >
                    <div className={`shrink-0 h-7 w-7 rounded-md flex items-center justify-center ${
                      e.kind === "down" ? "bg-rose-500/15 text-rose-400" : "bg-emerald-500/15 text-emerald-400"
                    }`}>
                      {e.kind === "down" ? <WifiOff className="h-3.5 w-3.5" /> : <Wifi className="h-3.5 w-3.5" />}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-semibold text-ink-primary truncate">
                        {e.kind === "down" ? "Connection lost" : "Restored"} · {e.label}
                      </div>
                      {e.detail && <div className="text-xs text-ink-secondary line-clamp-2">{e.detail}</div>}
                      <div className="text-[10px] font-mono text-ink-muted mt-0.5">{timeAgo(e.created_at)}</div>
                    </div>
                  </button>
                ))}
              </div>
            )}
            {(!data.events || data.events.length === 0) ? (
              <div className="px-4 py-8 text-center">
                <Sparkles className="h-6 w-6 text-brand-amber mx-auto mb-2" />
                <p className="text-xs text-ink-muted">No sponsor decisions yet.<br />Send a preview to start hearing back.</p>
              </div>
            ) : (
              data.events.map((e) => (
                <button
                  key={`${e.commercial_id}_${e.decided_at}`}
                  onClick={() => {
                    setOpen(false);
                    navigate(`/dashboard/commercials?focus=${e.commercial_id}`);
                  }}
                  data-testid={`notification-event-${e.commercial_id}`}
                  className="w-full text-left px-4 py-3 border-b border-line/60 hover:bg-bg-elevated/60 transition flex items-start gap-3"
                >
                  <div className={`shrink-0 h-7 w-7 rounded-md flex items-center justify-center ${
                    e.status === "approved"
                      ? "bg-emerald-500/15 text-emerald-400"
                      : "bg-rose-500/15 text-rose-400"
                  }`}>
                    {e.status === "approved" ? <CheckCircle2 className="h-3.5 w-3.5" /> : <XCircle className="h-3.5 w-3.5" />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold text-ink-primary truncate">
                      {e.status === "approved" ? "Approved" : "Changes requested"} · {e.advertiser}
                    </div>
                    {e.feedback && (
                      <div className="text-xs text-ink-secondary italic line-clamp-2">&ldquo;{e.feedback}&rdquo;</div>
                    )}
                    <div className="text-[10px] font-mono text-ink-muted mt-0.5">{timeAgo(e.decided_at)}</div>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
